<template>
  <div :class="['product-price', cssClasses, defaultPriceClass, authenticatedUserPriceClass, campaignPriceClass]">
    <div v-if="!isArticle || showListPrice" :class="['product-price__text', { 'product-price__text--blue-plate': showListPrice }]">
      <template v-if="!isArticle">
        {{ tStatic('fromPriceLabel') }}
      </template>

      <template v-else>
        {{ tStatic('listPriceLabel') }} {{ formatPrice(listPrice) }}
      </template>
    </div>

    <div v-element-loader="{ endpoint: 'productListPrices', matcher: loaderMatcher, useSmallLoader: true }" class="product-price__main">
      <template v-if="!isArticle && lowestPrice > 0">
        {{ formatPrice(lowestPrice) }}
      </template>

      <template v-if="isArticle && !hasCampaignPrice && customerPrice > 0">
        {{ formatPrice(customerPrice) }}
      </template>

      <template v-if="isArticle && hasCampaignPrice && customerPrice > 0">
        {{ formatPrice(campaignPrice) }}
        <span class="product-price__reduced">{{ formatPrice(customerPrice) }}</span>
      </template>
    </div>

    <div v-if="isArticle && hasCampaignPrice" class="product-price__text product-price__text--campaign">
      {{ tStatic('campaignPrice') }}
    </div>

    <div class="product-price__text product-price__text--vat">
      {{ useVat ? tStatic('priceIncludingVat') : tStatic('priceExcludingVat') }}
    </div>

    <template v-if="showBracketPrices">
      <div v-for="(item, i) in bracketPrices" :key="i" class="product-price__bracket-prices">
        <span class="product-price">{{ tStatic('bracketPriceText').replace('{0}', item.quantity).replace('{1}', formatPrice(item.price)) }}</span>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

export default {
  props: {
    currentObject: {
      type: Object,
      required: true
    },
    isProduct: {
      type: Boolean,
      required: true
    },
    showBracketPrices: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    ...mapGetters('ui', ['useVat']),
    currentObjectItemCount() {
      return _.get(this, 'currentObject.items.length')
    },
    current() {
      if(this.isProduct) {
        if(this.currentObjectItemCount === 1) {
          return this.currentObject.items[0]
        }
      }
      return this.currentObject
    },
    hasPrice() {
      return this.current.customerPrice !== null && this.current.customerPrice !== undefined
    },
    lowestPrice() {
      if(this.isProduct) {
        return this.current.lowestPrice
      }
      return ''
    },
    customerPrice() {
      if(!this.hasPrice) {
        return ''
      }

      if(this.current.customerPrice.customerPrice !== undefined) {
        return this.current.customerPrice.customerPrice
      } else {
        return this.current.customerPrice.listPrice
      }
    },
    hasCampaignPrice() {
      if(!this.hasPrice) {
        return ''
      }

      if(this.current.customerPrice !== null) {
        return this.current.customerPrice.campaignPrice !== undefined
      }

      return false
    },
    campaignPrice() {
      if(!this.hasPrice) {
        return ''
      }

      if(this.current.customerPrice !== null) {
        return this.current.customerPrice.campaignPrice
      }

      return ''
    },
    priceReduction() {
      return this.customerPrice - this.campaignPrice
    },
    countItems() {
      if(this.isProduct) {
        return this.currentObjectItemCount
      }

      return 1
    },
    listPrice() {
      if(!this.hasPrice) {
        return ''
      }

      return this.current.customerPrice.listPrice
    },
    showListPrice() {
      return !this.isProductOwner && this.listPrice !== this.customerPrice
    },
    isArticle() {
      return this.countItems === 1
    },
    bracketPrices() {
      return this.current.customerPrice
        ? this.current.customerPrice.bracketPrices
        : []
    },
    isAuthenticated() {
      return this.clientModel.isAuthenticated
    },
    defaultPriceClass() {
      return !this.isAuthenticated && !this.hasCampaignPrice && 'product-price--default'
    },
    authenticatedUserPriceClass() {
      return this.isAuthenticated && !this.hasCampaignPrice && 'product-price--authenticated-user'
    },
    campaignPriceClass() {
      return this.hasCampaignPrice && 'product-price--campaign'
    }
  },
  methods: {
    loaderMatcher(data) {
      return _.chain(data).get('productId', []).includes(_.get(this, 'currentObject.id')).value()
    }
  }
}
</script>

<style lang="scss">
.product-price {
  @include caption;
}

.product-price--default {
  color: color(text-brand-blue);
}

.product-price--authenticated-user {
  color: color(text-blue);
}

.product-price--campaign {
  color: color(text-red);
}

.product-price__top {
  height: 0.8125rem;
  margin-bottom: measure(eighth);
}

.product-price__text {
  @include caption-semi-bold;
  margin-bottom: measure(eighth);
}

.product-price__text--campaign {
  color: color(text-red);
  margin-bottom: 0;
}

.product-price__text--blue-plate {
  display: inline-block;
  padding: measure(eighth) measure(quarter);
  background-color: color(blue-5);
  color: color(text-brand-blue);
  border-radius: 0.1875rem;
}

.product-price__text--vat {
  @include caption;
  margin-bottom: 0;
}

.product-price__main {
  @include h2-bold;
  min-height: 1.4rem;
}

.product-price__reduced {
  @include caption;
  text-decoration: line-through;
  color: color(text)
}

.product-price__bracket-prices {
  margin-top: measure(quarter);
}
</style>
